import { Typography } from '@material-ui/core';
import React, { useState } from 'react';
import { Check } from 'lucide-react';
import { AiVisibilityAccessDialog } from '../components/AiVisibilityAccessDialog';
import { Headline } from '../components/Headline';
import { Kicker } from '../components/Kicker';
import { Reveal } from '../components/motion';
import { ComingSoonBadge } from '../components/NewBadge';
import { SEO } from '../components/SEO';
import MainLayout from '../layouts/main';
import styled from '../styled';
import { COLORS } from '../themes/color';

const PageHero = styled('div')`
  text-align: center;
  max-width: 860px;
  margin: 140px auto 80px;
  padding: 0 24px;

  @media (max-width: 800px) {
    margin: 100px auto 60px;
  }
`;

const BadgeRow = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 20px;
`;

const HeroDescription = styled('p')`
  font-size: 19px;
  line-height: 1.6;
  color: #4f4f4f;
  max-width: 720px;
  margin: 24px auto 36px;
`;

const RequestAccessButton = styled('button')`
  background-color: #0e1c2b;
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  border: none;
  border-radius: 100px;
  padding: 14px 32px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #1f3348;
  }
`;

const Positioning = styled('div')`
  max-width: 860px;
  margin: 0 auto 100px;
  padding: 0 24px;
`;

const PositioningInner = styled('blockquote')`
  border-left: 4px solid ${COLORS.PRIMARY};
  margin: 0;
  padding: 8px 0 8px 28px;
  font-size: 22px;
  line-height: 1.5;
  color: #0e1c2b;
  font-weight: 500;

  @media (max-width: 600px) {
    font-size: 18px;
  }
`;

const JourneySection = styled('section')`
  max-width: 1100px;
  margin: 0 auto 40px;
  padding: 0 24px;
`;

const StepGrid = styled('div')`
  display: grid;
  grid-template-columns: 88px 1fr;
  gap: 32px;
  margin-bottom: 72px;
  align-items: start;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const StepNumber = styled('div')`
  font-family: 'SF Mono', 'Roboto Mono', 'Fira Mono', monospace;
  font-size: 40px;
  font-weight: 700;
  color: ${COLORS.PRIMARY};
  line-height: 1;
  padding-top: 6px;
`;

const StepContent = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 14px;
  align-items: flex-start;
`;

const StepTitleRow = styled('div')`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const StepBody = styled('p')`
  font-size: 17px;
  line-height: 1.65;
  color: #4f4f4f;
  margin: 0;
  max-width: 720px;
`;

const CheckList = styled('ul')`
  list-style: none;
  padding: 0;
  margin: 4px 0 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const CheckItem = styled('li')`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  font-size: 15px;
  line-height: 1.5;
  color: #4f4f4f;

  svg {
    flex-shrink: 0;
    margin-top: 3px;
    color: ${COLORS.PRIMARY};
  }
`;

const PackageRow = styled('div')`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-top: 8px;
  width: 100%;

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`;

const PackageCard = styled('div')`
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 18px;

  h4 {
    font-size: 15px;
    font-weight: 700;
    color: #0e1c2b;
    margin: 0 0 6px;
  }

  p {
    font-size: 13px;
    line-height: 1.5;
    color: #64748b;
    margin: 0;
  }
`;

const FinalCta = styled('div')`
  text-align: center;
  max-width: 700px;
  margin: 40px auto 120px;
  padding: 0 24px;
`;

export default () => {
  const [dialogOpen, setDialogOpen] = useState(false);

  return (
    <MainLayout>
      <div>
        <SEO
          pathname="/ai-visibility/"
          title="AI Visibility Campaigns for Publishers"
          description="Discover where your influence is building in AI answers, pitch brands sellable packages shaped by your editorial practices, and execute campaigns with your team. Coming soon. Request early access."
        />

        <PageHero>
          <Reveal>
            <Kicker>AI visibility campaigns</Kicker>
            <BadgeRow>
              <ComingSoonBadge />
            </BadgeRow>
            <Headline component="h1">
              Monetize your influence in AI answers
            </Headline>
            <HeroDescription>
              AI assistants already rely on your content to recommend
              products. AI visibility campaigns help you turn that influence
              into a new revenue line, from discovering the opportunity to
              pitching the brand to running the campaign.
            </HeroDescription>
            <RequestAccessButton onClick={() => setDialogOpen(true)}>
              Request early access
            </RequestAccessButton>
          </Reveal>
        </PageHero>

        <Positioning>
          <Reveal>
            <PositioningInner>
              Most AI visibility tools are built for brands watching their own
              mentions. Affilimate is built for publishers who shape the
              answers.
            </PositioningInner>
          </Reveal>
        </Positioning>

        <JourneySection>
          <StepGrid>
            <Reveal>
              <StepNumber>01</StepNumber>
            </Reveal>
            <Reveal delay={100}>
              <StepContent>
                <StepTitleRow>
                  <Headline component="h2" variant="h3" style={{ margin: 0 }}>
                    Discover your opportunities
                  </Headline>
                </StepTitleRow>
                <StepBody>
                  Agent analytics for commerce content shows you where your
                  influence is building, powered by our product catalog
                  spanning tens of millions of products and thousands of
                  brands, matched against the content you already monetize.
                </StepBody>
                <CheckList>
                  <CheckItem>
                    <Check size={16} />
                    See which of your pages AI systems rely on, next to the
                    revenue those pages already earn
                  </CheckItem>
                  <CheckItem>
                    <Check size={16} />
                    Spot the brands your influence touches most
                  </CheckItem>
                  <CheckItem>
                    <Check size={16} />
                    Prompt tracking: see how you show up in the questions
                    people actually ask (coming soon)
                  </CheckItem>
                </CheckList>
              </StepContent>
            </Reveal>
          </StepGrid>

          <StepGrid>
            <Reveal>
              <StepNumber>02</StepNumber>
            </Reveal>
            <Reveal delay={100}>
              <StepContent>
                <StepTitleRow>
                  <Headline component="h2" variant="h3" style={{ margin: 0 }}>
                    Pitch brands packages they can buy
                  </Headline>
                </StepTitleRow>
                <StepBody>
                  Our prospecting tool turns your opportunities into concrete
                  offers, shaped by your editorial practices, so you&apos;re
                  pitching work your newsroom is actually willing to do.
                </StepBody>
                <PackageRow>
                  <PackageCard>
                    <h4>New content</h4>
                    <p>
                      Commission fresh coverage in categories where your
                      influence is strongest.
                    </p>
                  </PackageCard>
                  <PackageCard>
                    <h4>New inclusions</h4>
                    <p>
                      Add the brand to existing content that AI systems already
                      trust.
                    </p>
                  </PackageCard>
                  <PackageCard>
                    <h4>AEO optimization</h4>
                    <p>
                      Strengthen existing mentions so they perform better in
                      AI answers.
                    </p>
                  </PackageCard>
                </PackageRow>
              </StepContent>
            </Reveal>
          </StepGrid>

          <StepGrid>
            <Reveal>
              <StepNumber>03</StepNumber>
            </Reveal>
            <Reveal delay={100}>
              <StepContent>
                <StepTitleRow>
                  <Headline component="h2" variant="h3" style={{ margin: 0 }}>
                    Execute and track your influence
                  </Headline>
                </StepTitleRow>
                <StepBody>
                  When a brand says yes, the pitch becomes a plan. Every action
                  step converts into a checklist your team works through, and
                  you track how your influence develops as the campaign runs.
                </StepBody>
                <CheckList>
                  <CheckItem>
                    <Check size={16} />
                    Team checklists generated from the package you sold
                  </CheckItem>
                  <CheckItem>
                    <Check size={16} />
                    Campaign execution on the same infrastructure as your
                    sponsored content campaigns
                  </CheckItem>
                  <CheckItem>
                    <Check size={16} />
                    Influence tracking over the life of the campaign
                  </CheckItem>
                </CheckList>
              </StepContent>
            </Reveal>
          </StepGrid>
        </JourneySection>

        <FinalCta>
          <Reveal>
            <Headline component="h2">Request early access</Headline>
            <Typography
              variant="body1"
              color="textSecondary"
              style={{ margin: '16px auto 32px' }}
            >
              We&apos;re onboarding publishers to AI visibility campaigns in
              small groups ahead of launch. Tell us about your site and we&apos;ll
              be in touch.
            </Typography>
            <RequestAccessButton onClick={() => setDialogOpen(true)}>
              Request early access
            </RequestAccessButton>
          </Reveal>
        </FinalCta>

        <AiVisibilityAccessDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
        />
      </div>
    </MainLayout>
  );
};
